import { useCallback, useEffect, useRef, useState } from 'react';
import { fingeringApi } from '../services/fingeringApi';
import type { JobStatusValue } from '../services/fingeringApi';
import type { FingeringMode } from '../types/fingering';
import {
  saveFingeringResult,
  loadFingeringResult,
  loadPendingOriginalPdf,
  clearPendingOriginalPdf,
} from '../utils/fingeringStorage';
import {
  listJobLifecycleEntries,
  clearJobLifecycleEntry,
  hasPendingSubmitMarker,
  type JobType,
  type JobLifecycleEntry,
} from '../utils/jobLifecycleStorage';
import { useJobStatusPolling } from './useJobStatusPolling';
import { JOB_LIFECYCLE_TIMEOUT_MS } from '../constants/timeouts';
import { isTracking as jobTrackerIsTracking } from '../services/jobTracker';

/** リロード復元時、元ファイル名が不明なジョブ結果に付与するフォールバック名。 */
const RESUMED_RESULT_FILE_NAME = '運指結果.pdf';

/**
 * done 判定後の getJobResult が transient に失敗したときの retry 回数 (issue #36 F-2c)。
 * 複数インスタンス / 結果伝播遅延で done 直後に一瞬 result が取れないことがあるため、
 * 即 entry を消さず数回 retry する。全 attempt 失敗で初めて確定失敗扱いにし entry を掃除する
 * (無限 loop 防止 issue #25 B-1 を維持)。
 */
const GET_RESULT_MAX_ATTEMPTS = 3;
/** getJobResult retry 間隔 (ミリ秒)。 */
const GET_RESULT_RETRY_INTERVAL_MS = 300;

/**
 * watchdog (issue #36 パターンA + #72): 投入 (entry.createdAt) からこの時間を過ぎても
 * processing/queued のまま終端しなければタイムアウト確定とする (ゾンビジョブ防御)。
 *
 * 値は共通定数 `JOB_LIFECYCLE_TIMEOUT_MS` (30 分、 score-engine JobManager.JOB_TTL と同期) から参照。
 * - #65 page-split パイプラインで大型 PDF (~600s) + 自動保存 + Drive upload + バッファ余裕
 * - 「server が応答できる間は frontend が諦めない」 semantics (#72)
 * - 30 分超過時は server 側でも _is_expired で 404 を返し始める → 構造的に expired 経路に倒れる
 * - 旧値 10 分 (#36 当時) → 30 分 (#72 で #65 大型 PDF 対応)
 *
 * これはゾンビジョブ (server が status を確定できない異常系) の最後の砦であり、
 * 「処理が長い」 を理由に発火させない (#72 設計原則)。
 * #75 で導入する STALE_ENTRY_THRESHOLD_MS も同一定数を共有する (constants/timeouts.ts)。
 */
const MAX_TOTAL_PROCESSING_MS = JOB_LIFECYCLE_TIMEOUT_MS;

/**
 * #75: mount 時 / visibility 復帰時の同期 stale 判定上限。
 * `entry.createdAt` から本値を超過した entry は polling せず即 cleanup する。
 * server 側 JOB_TTL (30 分) と一致させ、 「server で取り出せないと確定したジョブ」
 * を frontend が抱え続けないようにする (R5: 12 時間 processing バグの真因解消)。
 */
const STALE_ENTRY_THRESHOLD_MS = JOB_LIFECYCLE_TIMEOUT_MS;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * 復元失敗の種別 (issue #36 M-2 + #72 + #75)。 FingeringPage が文言を出し分けるために使う。
 * - 'expired': ジョブの有効期限切れ (404→expired が budget 超過で確定)。
 * - 'failed': server が failed/error_processing を返した (確定エラー)。
 * - 'fetch': done 後の結果取得 (getJobResult) が全 attempt 失敗。
 * - 'watchdog': frontend watchdog (#72) 確定。 server は応答不能 (ゾンビ)。
 * - 'stale': mount 時 / visibility 復帰時に entry が 30 分超過していて即 cleanup (#75)。
 *            server で取り出せないことが確実な entry を polling せず中立的に通知する。
 */
export type ResumeErrorKind = 'expired' | 'failed' | 'fetch' | 'watchdog' | 'stale';

export interface UseJobLifecycleResumeResult {
  /** 復元対象の jobId。進行中ジョブが無いときは null。 */
  jobId: string | null;
  /** 復元対象の job_type。job_type 別の UI 復元に使う。 */
  jobType: JobType | null;
  /** 復元した JobLifecycleEntry。進行中ジョブが無いときは null。params 参照に使う。 */
  entry: JobLifecycleEntry | null;
  /** ポーリング中のジョブ状態。 */
  status: JobStatusValue | null;
  /** キュー順番待ち位置。進行中ジョブが無い / 取得前は undefined。 */
  queuePosition?: number;
  /** done を待っている復元処理が進行中かどうか。 */
  resuming: boolean;
  /** 復元失敗 (failed / expired / 取得エラー) 時のメッセージ。 */
  error: string | null;
  /** 復元失敗の種別。 error が null のときは null (issue #36 M-2)。 */
  errorKind: ResumeErrorKind | null;
  /**
   * handleDone の saveFingeringResult 完了後に true になるフラグ。
   * FingeringPage はこの edge で loadAndShowResult をトリガする (issue #7)。
   */
  restored: boolean;
  /**
   * pre-jobID 区間 (POST /fingering/jobs アップロード中) reload を検知したか。
   * mount 初回 render で同期評価する (issue #9 reload race)。
   */
  hasPendingSubmit: boolean;
}

/**
 * リロード/タブ切替後に進行中ジョブを復元する hook。
 * マウント時に jobLifecycleStorage から進行中ジョブを 1 件読み、
 * useJobStatusPolling でポーリングを再開する。done なら結果を取得して
 * IndexedDB へ保存し、lifecycle エントリを削除する。
 */
const STALE_ERROR_MESSAGE = '前回の処理は完了しているか中断されました';

/**
 * #75: 進行中ジョブを listJobLifecycleEntries() から同期的に拾い、 30 分超過なら同期 cleanup する。
 * 戻り値は「使える entry」 と「stale 判定の真偽」 のペア。 mount 時 lazy initializer 用で、
 * entry/error/errorKind の初期化が同じ判定を参照できるよう 1 度だけ評価する。
 */
function readActiveEntryWithStaleCheck(): { entry: JobLifecycleEntry | null; stale: boolean } {
  if (typeof window === 'undefined') return { entry: null, stale: false };
  const found = listJobLifecycleEntries().find(
    (job) => job.status === 'queued' || job.status === 'processing',
  );
  if (!found) return { entry: null, stale: false };
  // issue #76 Phase 4-α: fire-and-forget (ON 経路) で投入された entry は jobTracker singleton 管理。
  // FingeringPage 側で resume として扱わず、 履歴 popup + DriveFooterBar バッジで追跡する設計。
  // jobTrackerIsTracking ガードより前にチェック (mount 同期評価で singleton bootstrap 未走の race を回避)。
  if (found.driveSaveTarget !== undefined) return { entry: null, stale: false };
  // issue #76 Phase 2: singleton bootstrap で既に jobTracker が拾った jobId は二重 resume しない
  if (jobTrackerIsTracking(found.jobId)) return { entry: null, stale: false };
  if (Date.now() - found.createdAt > STALE_ENTRY_THRESHOLD_MS) {
    clearJobLifecycleEntry(found.jobId);
    void clearPendingOriginalPdf(found.jobId);
    return { entry: null, stale: true };
  }
  return { entry: found, stale: false };
}

export function useJobLifecycleResume(): UseJobLifecycleResumeResult {
  // 復元した進行中ジョブの entry。jobId / jobType / params はここから参照する。
  // lazy initializer で初回 render の同期評価から値を立てる (issue #9 バグ A)。
  // #75: ここで 30 分超過 entry を同期 cleanup し、 polling 一切走らせない (R5 解消)。
  // entry/error/errorKind の lazy init は同じ判定を参照する必要があるため、
  // useRef を使って 1 度だけ評価し全 useState で共有する。
  const initialReadRef = useRef<{ entry: JobLifecycleEntry | null; stale: boolean } | null>(null);
  if (initialReadRef.current === null) {
    initialReadRef.current = readActiveEntryWithStaleCheck();
  }
  const initialRead = initialReadRef.current;
  const [entry, setEntry] = useState<JobLifecycleEntry | null>(initialRead.entry);
  const [resuming, setResuming] = useState<boolean>(initialRead.entry !== null);
  const [error, setError] = useState<string | null>(
    initialRead.stale ? STALE_ERROR_MESSAGE : null,
  );
  const [errorKind, setErrorKind] = useState<ResumeErrorKind | null>(
    initialRead.stale ? 'stale' : null,
  );
  // saveFingeringResult 完了後に true。FingeringPage はこの edge で結果カードへ反映する。
  const [restored, setRestored] = useState(false);
  // pre-jobID 区間 (POST /jobs アップロード中) reload の検知 (issue #9 reload race)。
  // entry と同じく lazy initializer で初回 render の同期評価から値を立てる。
  // mount 時点で pre-jobID 区間判定は確定するため、後から変化させる必要はない。
  const [hasPendingSubmit] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return hasPendingSubmitMarker();
  });

  const jobId = entry?.jobId ?? null;
  const jobType = entry?.jobType ?? null;

  const handleDone = useCallback(async (doneJobId: string) => {
    try {
      // done 直後の getJobResult は transient (結果伝播遅延 / 複数インスタンス) で
      // 失敗しうる。即 entry を消さず数回 retry し、全失敗で初めて確定失敗扱いにする (issue #36 F-2c)。
      let result: Awaited<ReturnType<typeof fingeringApi.getJobResult>> | null = null;
      let lastError: unknown = null;
      for (let attempt = 0; attempt < GET_RESULT_MAX_ATTEMPTS; attempt++) {
        try {
          result = await fingeringApi.getJobResult(doneJobId);
          break;
        } catch (err) {
          lastError = err;
          if (attempt < GET_RESULT_MAX_ATTEMPTS - 1) {
            await sleep(GET_RESULT_RETRY_INTERVAL_MS);
          }
        }
      }
      if (result === null) {
        throw lastError instanceof Error
          ? lastError
          : new Error('ジョブ結果の復元に失敗しました');
      }
      // 投入時に params へ持ち越した元 PDF 由来の結果名を使う。
      // 無いとき (旧 entry / 名前未保存) のみフォールバック名 (async reload F4)。
      const fileName = entry?.params?.fileName ?? RESUMED_RESULT_FILE_NAME;
      // 投入時に先行保存した元 PDF を読み出し originalPdfBlob に合流させる。
      // job result には元 PDF が含まれないため (async reload F6、設計 §5.2 案A)。
      const originalPdfBlob = (await loadPendingOriginalPdf(doneJobId)) ?? undefined;
      await saveFingeringResult({
        pdfBlob: result.pdfBlob,
        fileName,
        audioBlob: result.audioBlob,
        audioFileName: result.audioFileName,
        fingeringJson: result.fingeringJson,
        originalPdfBlob,
        mxlBlob: result.mxlBlob,
        detectedBpm: result.detectedBpm,
        // issue #81: fire-and-forget 復帰でも mode を永続化し Drive 保存ボタンを再 enable する。
        // JobLifecycleParams.mode は string 型 (実値は 'part'|'score') のため FingeringMode へ narrow。
        mode: entry?.params?.mode as FingeringMode | undefined,
      });
      clearJobLifecycleEntry(doneJobId);
      // 結果に合流済みの先行保存元 PDF は掃除する。
      await clearPendingOriginalPdf(doneJobId);
      // 復元データが IndexedDB へ確実に書き込まれたタイミングで通知する。
      // finally に置くと catch 時にも誤通知してしまうため try ブロック末尾で呼ぶ (issue #7)。
      setRestored(true);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'ジョブ結果の復元に失敗しました';
      setError(message);
      setErrorKind('fetch');
      // F-1 (issue #25 B-1): result 取得失敗時も entry を掃除して reload 時の無限 loop を断ち切る
      clearJobLifecycleEntry(doneJobId);
      void clearPendingOriginalPdf(doneJobId);
    } finally {
      setResuming(false);
    }
  }, [entry]);

  // handleDone は async。useJobStatusPolling の onDone は同期コールバックなので ref 経由で呼ぶ。
  const handleDoneRef = useRef(handleDone);
  useEffect(() => {
    handleDoneRef.current = handleDone;
  }, [handleDone]);

  const handleError = useCallback(async (
    message: string,
    kind: 'expired' | 'failed' | 'watchdog' | 'stale',
  ) => {
    // パターンB (issue #36 #69): expired のみ IndexedDB に done 済み結果があれば error を出さず復元表示。
    // 'watchdog' (#72) は server 応答不能想定だが、 race 救済は useJobStatusPolling の最終 polling 側で
    // 先行実施済のため、 ここで IndexedDB 確認は不要 (handleError 到達 = 確定エラー)。
    // 'failed' (server 確定エラー) は結果が無い前提で従来通り緩和しない。
    if (kind === 'expired') {
      const stored = await loadFingeringResult();
      if (stored) {
        // 結果は手元にある → polling 終了 + entry 掃除しつつ、 restored で結果カードを表示させる。
        setResuming(false);
        setEntry((current) => {
          if (current) {
            clearJobLifecycleEntry(current.jobId);
            void clearPendingOriginalPdf(current.jobId);
          }
          return current;
        });
        setRestored(true);
        return;
      }
    }
    // 結果なし (expired で消失) / failed → 従来通り明示エラー + entry 掃除 (#29 M-2 導線)。
    setError(message);
    setErrorKind(kind);
    setResuming(false);
    setEntry((current) => {
      if (current) {
        clearJobLifecycleEntry(current.jobId);
        // 失敗ジョブの先行保存元 PDF も掃除する (async reload F6)。
        void clearPendingOriginalPdf(current.jobId);
      }
      return current;
    });
  }, []);

  // handleError は async (パターンB で loadFingeringResult を await)。 ref 経由で呼ぶ。
  const handleErrorRef = useRef(handleError);
  useEffect(() => {
    handleErrorRef.current = handleError;
  }, [handleError]);

  // #75 系統 3: タブ復帰 (visibility=visible) で entry の age を再判定し、
  // 30 分超過なら同期 cleanup する (mount 同期判定の二重防御)。
  // 既に stale で cleanup 済 (entry=null) や、 期限内 entry なら no-op。
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handler = () => {
      if (document.visibilityState !== 'visible') return;
      if (!entry) return;
      if (Date.now() - entry.createdAt <= STALE_ENTRY_THRESHOLD_MS) return;
      clearJobLifecycleEntry(entry.jobId);
      void clearPendingOriginalPdf(entry.jobId);
      setEntry(null);
      setResuming(false);
      setError(STALE_ERROR_MESSAGE);
      setErrorKind('stale');
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [entry]);

  // watchdog (issue #36 パターンA): 投入からの絶対上限時刻。 entry が無ければ無効 (undefined)。
  const deadlineAt = entry ? entry.createdAt + MAX_TOTAL_PROCESSING_MS : undefined;

  const polling = useJobStatusPolling(jobId, {
    onDone: (doneJobId) => {
      void handleDoneRef.current(doneJobId);
    },
    onError: (msg, kind) => {
      void handleErrorRef.current(msg, kind);
    },
    deadlineAt,
  });

  return {
    jobId,
    jobType,
    entry,
    status: polling.status,
    queuePosition: polling.queuePosition,
    resuming,
    error,
    errorKind,
    restored,
    hasPendingSubmit,
  };
}
